import React, { useState } from 'react';
import { 
  Settings as SettingsIcon, 
  Key, 
  Cloud, 
  Video, 
  Users, 
  Shield, 
  Save,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Info
} from 'lucide-react';
import { useStream } from '../context/StreamContext';
import { cloudflareStreamService } from '../services/cloudflareStream';

function Settings() {
  const { settings, dispatch } = useStream();
  
  const [formData, setFormData] = useState({
    apiKey: settings.apiKey || '',
    accountId: settings.accountId || '',
    defaultQuality: settings.defaultQuality || '720p',
    enableChat: settings.enableChat,
    enableRecording: settings.enableRecording,
    maxBitrate: '5000',
    bufferSize: '3',
    enableNotifications: true,
    enableAnalytics: true,
    enableAutostart: false,
    enableLowLatency: true
  });
  
  const [showApiKey, setShowApiKey] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    dispatch({ 
      type: 'UPDATE_SETTINGS', 
      payload: formData 
    });
    
    // Update Cloudflare service credentials
    if (formData.accountId && formData.apiKey) {
      cloudflareStreamService.setCredentials(formData.accountId, formData.apiKey);
    }
    
    setHasUnsavedChanges(false);
  };

  const testConnection = async () => {
    if (!formData.accountId || !formData.apiKey) {
      setConnectionStatus({ type: 'error', message: 'Please enter both Account ID and API Key' });
      return;
    }

    setTestingConnection(true);
    setConnectionStatus(null);

    try {
      // Temporarily set credentials for testing
      cloudflareStreamService.setCredentials(formData.accountId, formData.apiKey);
      
      // Try to fetch streams to test the connection
      await cloudflareStreamService.getStreams();
      
      setConnectionStatus({ 
        type: 'success', 
        message: 'Connection successful! Your Cloudflare Stream API credentials are valid.' 
      });
    } catch (error) {
      setConnectionStatus({ 
        type: 'error', 
        message: `Connection failed: ${error.message || 'Invalid credentials or network error'}` 
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const resetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      setFormData({
        apiKey: '',
        accountId: '',
        defaultQuality: '720p',
        enableChat: true,
        enableRecording: true,
        maxBitrate: '5000',
        bufferSize: '3',
        enableNotifications: true,
        enableAnalytics: true,
        enableAutostart: false,
        enableLowLatency: true
      });
      setHasUnsavedChanges(true);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
          <p className="text-gray-400">Configure your Cloudflare Stream integration and app preferences</p>
        </div>
        
        <div className="flex items-center space-x-4">
          {hasUnsavedChanges && (
            <span className="text-yellow-400 text-sm flex items-center space-x-1">
              <AlertTriangle className="w-4 h-4" />
              <span>Unsaved changes</span>
            </span>
          )}
          
          <button
            onClick={handleSave}
            disabled={!hasUnsavedChanges}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            <span>Save Settings</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Cloudflare API Configuration */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-cf-orange rounded-lg flex items-center justify-center">
                <Cloud className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Cloudflare Stream API</h3>
                <p className="text-gray-400 text-sm">Configure your Cloudflare Stream credentials</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Account ID
                </label>
                <input
                  type="text"
                  value={formData.accountId}
                  onChange={(e) => handleInputChange('accountId', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cf-orange focus:border-transparent"
                  placeholder="Your Cloudflare Account ID"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Find this in your Cloudflare dashboard under Account ID
                </p>
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  API Token
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={formData.apiKey}
                    onChange={(e) => handleInputChange('apiKey', e.target.value)}
                    className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cf-orange focus:border-transparent"
                    placeholder="Your Cloudflare API Token"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Create an API token with Stream:Edit permissions
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={testConnection}
                  disabled={testingConnection || !formData.accountId || !formData.apiKey}
                  className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testingConnection ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Testing...</span>
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4" />
                      <span>Test Connection</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* Connection Status */}
              {connectionStatus && (
                <div className={`p-3 rounded-lg border-l-4 ${
                  connectionStatus.type === 'success'
                    ? 'bg-green-500 bg-opacity-10 border-green-500 text-green-400'
                    : 'bg-red-500 bg-opacity-10 border-red-500 text-red-400'
                }`}>
                  <div className="flex items-center space-x-2">
                    {connectionStatus.type === 'success' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <AlertTriangle className="w-4 h-4" />
                    )}
                    <span className="text-sm">{connectionStatus.message}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Streaming Settings */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Video className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Streaming Settings</h3>
                <p className="text-gray-400 text-sm">Configure default streaming parameters</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Default Quality
                </label>
                <select
                  value={formData.defaultQuality}
                  onChange={(e) => handleInputChange('defaultQuality', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cf-orange focus:border-transparent"
                >
                  <option value="480p">480p</option>
                  <option value="720p">720p (HD)</option>
                  <option value="1080p">1080p (Full HD)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Max Bitrate (kbps)
                </label>
                <input
                  type="number"
                  value={formData.maxBitrate}
                  onChange={(e) => handleInputChange('maxBitrate', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cf-orange focus:border-transparent"
                  min="1000"
                  max="10000"
                />
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Buffer Size (seconds)
                </label>
                <input
                  type="number"
                  value={formData.bufferSize}
                  onChange={(e) => handleInputChange('bufferSize', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cf-orange focus:border-transparent"
                  min="1"
                  max="10"
                />
              </div>
            </div>
            
            <div className="space-y-3 mt-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableRecording"
                  checked={formData.enableRecording}
                  onChange={(e) => handleInputChange('enableRecording', e.target.checked)}
                  className="w-4 h-4 text-cf-orange bg-gray-700 border-gray-600 rounded focus:ring-cf-orange focus:ring-2"
                />
                <label htmlFor="enableRecording" className="ml-2 text-gray-300 text-sm">
                  Enable automatic recording of streams
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableLowLatency"
                  checked={formData.enableLowLatency}
                  onChange={(e) => handleInputChange('enableLowLatency', e.target.checked)}
                  className="w-4 h-4 text-cf-orange bg-gray-700 border-gray-600 rounded focus:ring-cf-orange focus:ring-2"
                />
                <label htmlFor="enableLowLatency" className="ml-2 text-gray-300 text-sm">
                  Enable low-latency streaming mode
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableAutostart"
                  checked={formData.enableAutostart}
                  onChange={(e) => handleInputChange('enableAutostart', e.target.checked)}
                  className="w-4 h-4 text-cf-orange bg-gray-700 border-gray-600 rounded focus:ring-cf-orange focus:ring-2"
                />
                <label htmlFor="enableAutostart" className="ml-2 text-gray-300 text-sm">
                  Auto-start streams when creating them
                </label>
              </div>
            </div>
          </div>

          {/* User Experience Settings */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">User Experience</h3>
                <p className="text-gray-400 text-sm">Configure viewer and host experience</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableChat"
                  checked={formData.enableChat}
                  onChange={(e) => handleInputChange('enableChat', e.target.checked)}
                  className="w-4 h-4 text-cf-orange bg-gray-700 border-gray-600 rounded focus:ring-cf-orange focus:ring-2"
                />
                <label htmlFor="enableChat" className="ml-2 text-gray-300 text-sm">
                  Enable chat for streams by default
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableNotifications"
                  checked={formData.enableNotifications}
                  onChange={(e) => handleInputChange('enableNotifications', e.target.checked)}
                  className="w-4 h-4 text-cf-orange bg-gray-700 border-gray-600 rounded focus:ring-cf-orange focus:ring-2"
                />
                <label htmlFor="enableNotifications" className="ml-2 text-gray-300 text-sm">
                  Enable browser notifications for stream events
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableAnalytics"
                  checked={formData.enableAnalytics}
                  onChange={(e) => handleInputChange('enableAnalytics', e.target.checked)}
                  className="w-4 h-4 text-cf-orange bg-gray-700 border-gray-600 rounded focus:ring-cf-orange focus:ring-2"
                />
                <label htmlFor="enableAnalytics" className="ml-2 text-gray-300 text-sm">
                  Enable analytics tracking for performance insights
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Setup Guide */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <Info className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">Quick Setup</h3>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-2">
                <span className="w-5 h-5 bg-cf-orange text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">1</span>
                <div>
                  <p className="text-white font-medium">Get Cloudflare Account ID</p>
                  <p className="text-gray-400">Found in your Cloudflare dashboard sidebar</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-2">
                <span className="w-5 h-5 bg-cf-orange text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">2</span>
                <div>
                  <p className="text-white font-medium">Create API Token</p>
                  <p className="text-gray-400">Use the "Custom token" template with Stream:Edit permissions</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-2">
                <span className="w-5 h-5 bg-cf-orange text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">3</span>
                <div>
                  <p className="text-white font-medium">Test Connection</p>
                  <p className="text-gray-400">Verify your credentials work correctly</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-2">
                <span className="w-5 h-5 bg-cf-orange text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">4</span>
                <div>
                  <p className="text-white font-medium">Configure Preferences</p>
                  <p className="text-gray-400">Set your default streaming options</p>
                </div>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="card border border-yellow-500 bg-yellow-500 bg-opacity-5">
            <div className="flex items-center space-x-3 mb-3">
              <Shield className="w-6 h-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-yellow-400">Security Notice</h3>
            </div>
            
            <div className="space-y-2 text-sm text-gray-300">
              <p>Your API credentials are stored locally in your browser and never sent to any third-party servers.</p>
              <p>For production use, consider using environment variables instead of storing credentials in the UI.</p>
            </div>
          </div>

          {/* Reset Settings */}
          <div className="card">
            <h3 className="text-lg font-semibold text-white mb-4">Danger Zone</h3>
            
            <button
              onClick={resetSettings}
              className="w-full bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Reset All Settings
            </button>
            
            <p className="text-gray-500 text-xs mt-2">
              This will reset all settings to their default values. This action cannot be undone.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings; 